import { Application } from 'pixi.js';
import { SceneManager } from './SceneManager';
import { initSafeArea, getInsets, onInsetsChange, type Insets } from '@/platform/safeArea';
import type { Scene } from './Scene';

const FIXED_STEP_MS = 1000 / 60;
const MAX_STEPS = 5;

/**
 * Device render profile. Strong devices get full DPR (sharp, capped at 3) and
 * MSAA; weak devices (few cores / little memory) are capped at DPR 2 with AA
 * off, so a high-DPR panel doesn't blow the fill-rate budget (the main source
 * of mobile heat). Heuristic from the device, not a guess.
 */
function deviceProfile(): { resolution: number; antialias: boolean } {
  const dpr = window.devicePixelRatio || 1;
  const cores = navigator.hardwareConcurrency ?? 4;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
  const weak = cores <= 4 || mem <= 3;
  return { resolution: Math.min(dpr, weak ? 2 : 3), antialias: !weak };
}

/**
 * Owns the PixiJS application, the fixed-timestep loop and the scene manager.
 * Everything gameplay-related is reached through the active scene.
 */
export class Game {
  readonly app: Application;
  readonly scenes: SceneManager;

  private accumulator = 0;
  private _insets: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

  private constructor(app: Application) {
    this.app = app;
    this.scenes = new SceneManager(this);

    this.app.ticker.add((ticker) => this.tick(ticker.deltaMS));

    const ro = new ResizeObserver(() => this.onResize());
    ro.observe(this.app.canvas.parentElement ?? this.app.canvas);
    window.addEventListener('resize', () => this.onResize());

    // Track device / Telegram safe-area insets and re-lay-out the UI when they
    // change (they arrive asynchronously after Telegram grants fullscreen).
    initSafeArea();
    this._insets = getInsets();
    onInsetsChange((i) => {
      this._insets = i;
      this.onResize();
    });
  }

  /** Current safe-area insets in CSS px (notch / status bar / home indicator). */
  get insets(): Insets {
    return this._insets;
  }

  static async create(mount: HTMLElement): Promise<Game> {
    const app = new Application();
    const profile = deviceProfile();
    await app.init({
      background: '#0c0a16',
      resizeTo: mount,
      // Adaptive: full DPR + MSAA on strong devices for sharpness; capped DPR
      // and AA off on weak ones so a DPR-3 panel doesn't push ~2.25× the pixels.
      antialias: profile.antialias,
      resolution: profile.resolution,
      autoDensity: true,
      powerPreference: 'high-performance',
    });
    mount.appendChild(app.canvas);
    return new Game(app);
  }

  /** Logical (CSS px) viewport size. */
  get width(): number {
    return this.app.screen.width;
  }
  get height(): number {
    return this.app.screen.height;
  }

  startWith(factory: (game: Game) => Scene): void {
    this.scenes.start(factory);
  }

  private tick(deltaMs: number): void {
    // Clamp huge frames (tab was backgrounded) to avoid a spiral of death.
    this.accumulator += Math.min(deltaMs, 100);
    let steps = 0;
    while (this.accumulator >= FIXED_STEP_MS && steps < MAX_STEPS) {
      this.scenes.update(FIXED_STEP_MS / 1000);
      this.accumulator -= FIXED_STEP_MS;
      steps++;
    }
    if (steps === MAX_STEPS) this.accumulator = 0;
  }

  private onResize(): void {
    this.scenes.resize(this.width, this.height);
  }
}
